<template>
  <div class="approval-path-container">
    <div class="flex items-center justify-between mb-3">
      <p class="text-sm font-medium text-gray-700">Approval Path:</p>
      <span
        v-if="status === 'approved'"
        class="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium"
      >
        Fully Approved
      </span>
      <span
        v-else-if="status === 'rejected'"
        class="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 font-medium"
      >
        Rejected
      </span>
      <span
        v-else-if="status === 'reversed'"
        class="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-medium"
      >
        Sent Back
      </span>
      <span
        v-else
        class="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 font-medium"
      >
        In Progress
      </span>
    </div>

    <!-- Empty state for new requests -->
    <div v-if="approvalPath.length === 0" class="text-center py-4">
      <div class="w-10 h-10 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-2">
        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      </div>
      <p class="text-sm text-gray-500">Awaiting first approval</p>
      <p class="text-xs text-gray-400 mt-1">Request will be routed to your direct manager</p>
    </div>

    <!-- Visual Timeline -->
    <div v-else class="relative">
      <!-- Progress Line -->
      <div class="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

      <!-- Steps -->
      <div class="space-y-3">
        <div
          v-for="(step, index) in approvalPath"
          :key="index"
          class="relative flex items-start pl-10"
        >
          <!-- Step Indicator -->
          <div
            class="absolute left-2 w-5 h-5 rounded-full flex items-center justify-center border-2 transition-all"
            :class="getStepIndicatorClass(step.status)"
          >
            <!-- Checkmark for approved -->
            <svg v-if="step.status === 'approved'" class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
            </svg>
            <!-- X for rejected -->
            <svg v-else-if="step.status === 'rejected'" class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
            <!-- Back arrow for reversed -->
            <svg v-else-if="step.status === 'reversed'" class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 111.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
            </svg>
            <!-- Clock for pending -->
            <svg v-else-if="step.status === 'pending'" class="w-3 h-3 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
            </svg>
          </div>

          <!-- Step Content -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center gap-2 min-w-0">
                <p class="text-sm font-medium truncate" :class="getStepTextClass(step.status)">
                  {{ step.approver?.name || 'Unknown Approver' }}
                </p>
                <span class="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 whitespace-nowrap">
                  {{ formatRole(step.role) }}
                </span>
              </div>
              <span
                class="text-xs px-2 py-0.5 rounded-full whitespace-nowrap"
                :class="getStatusBadgeClass(step.status)"
              >
                {{ step.status }}
              </span>
            </div>
            <p v-if="step.timestamp" class="text-xs text-gray-400 mt-0.5">
              {{ formatDateTime(step.timestamp) }}
            </p>
          </div>
        </div>

        <!-- Next approver indicator (if pending and not rejected) -->
        <div
          v-if="status === 'pending' && !hasRejection && hasPendingStep"
          class="relative flex items-start pl-10 opacity-60"
        >
          <div class="absolute left-2 w-5 h-5 rounded-full flex items-center justify-center border-2 border-dashed border-gray-300 bg-white">
            <svg class="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
            </svg>
          </div>
          <div class="flex-1">
            <p class="text-xs text-gray-500 italic">Next: Approver's manager in chain</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ApprovalPathVisualization',
  props: {
    amount: {
      type: Number,
      required: true
    },
    approvalPath: {
      type: Array,
      default: () => []
    },
    status: {
      type: String,
      default: 'pending'
    }
  },
  computed: {
    hasRejection() {
      return this.approvalPath.some(step => step.status === 'rejected');
    },
    hasPendingStep() {
      return this.approvalPath.some(step => step.status === 'pending');
    }
  },
  methods: {
    formatRole(role) {
      const roleMap = {
        'direct_manager': 'Manager',
        'hod': 'HoD',
        'managing_director': 'MD',
        'ceo': 'CEO',
        'finance': 'Finance',
        'admin': 'Admin',
        'employee': 'Employee'
      };
      return roleMap[role] || role;
    },
    getStepIndicatorClass(status) {
      switch (status) {
        case 'approved':
          return 'bg-green-500 border-green-500';
        case 'rejected':
          return 'bg-red-500 border-red-500';
        case 'reversed':
          return 'bg-amber-500 border-amber-500';
        case 'pending':
          return 'bg-yellow-100 border-yellow-500';
        default:
          return 'bg-white border-gray-300';
      }
    },
    getStepTextClass(status) {
      switch (status) {
        case 'approved':
          return 'text-green-700';
        case 'rejected':
          return 'text-red-700';
        case 'reversed':
          return 'text-amber-700';
        case 'pending':
          return 'text-yellow-700';
        default:
          return 'text-gray-500';
      }
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case 'approved':
          return 'bg-green-100 text-green-700';
        case 'rejected':
          return 'bg-red-100 text-red-700';
        case 'reversed':
          return 'bg-amber-100 text-amber-700';
        case 'pending':
          return 'bg-yellow-100 text-yellow-700';
        default:
          return 'bg-gray-100 text-gray-500';
      }
    },
    formatDateTime(dateString) {
      if (!dateString) return '';
      // Format date with time in CAT (Central Africa Time, GMT+2)
      return new Date(dateString).toLocaleString('en-RW', {
        timeZone: 'Africa/Kigali',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }) + ' CAT';
    }
  }
};
</script>

<style scoped>
.approval-path-container {
  @apply bg-gray-50 rounded-lg p-3 mt-2;
}
</style>
